import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { BID_CREATED_TYPE_FROM, BID_STATUS } from '@/shared/constants/bids';
import { PARTNER_OPTIONS } from '@/shared/constants/users';
import { fetchActiveCountries } from '@/shared/api/places';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { FilterDateTime } from '@/shared/ui/FilterDateTime';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { SortableTh } from '@/shared/ui/SortableTh';
import { UserProfileRedirect } from '@/shared/ui/UserProfileRedirect';
import { fetchCitiesByCountry, fetchUtmList } from '../users/api';
import {
  fetchBidsList,
  fetchCarMarks,
  fetchCarModels,
  fetchCarTypes,
  fetchOperatorWorkStatus,
  fetchPromoCodes,
  finishOperatorWork,
  showBidCustomerPhone,
  startOperatorWork,
  type BidsListParams,
} from './api';
import { cancelBid, setBidAtWork } from './detailApi';
import { BidEditModal } from './detail/BidEditModal';
import { CancelBidModal } from './detail/CancelBidModal';

const dateToUnix = (value: Date | null): number | '' =>
  value ? Math.floor(value.getTime() / 1000) : '';

interface NamedRef {
  id?: number | string;
  name?: string;
  phone?: string | null;
}

interface WorkTypeRef {
  title?: string;
}

interface BidRow extends Record<string, unknown> {
  id: number;
  type_id?: number;
  partner_id?: number;
  status_id?: number;
  status_change_time?: number;
  create_time?: number;
  final_date?: number;
  has_review?: number;
  request_type?: { title?: string } | null;
  auto?: { mark?: NamedRef | null; model?: NamedRef | null } | null;
  work_type?: WorkTypeRef[] | string | null;
  applied_discount?: { applied_discount_percent?: number; discount_name?: string } | null;
  is_created_from_call?: number;
  utm_source?: string;
  attached_promo_code?: { code?: string } | null;
  partner_name?: string;
  source?: string;
  customer?: NamedRef | null;
  city?: NamedRef | null;
  bid_source?: string;
  reject_reason?: Array<{ type?: string }> | null;
  /** First distributed service — used by «В работу» like legacy. */
  bid_distribution_services?: Array<{ service_id?: number | string }> | null;
}

type TabKey = 'unmoderated' | 'premoderation' | 'work' | 'finish' | 'cancel';

const TABS: Array<{ key: TabKey; label: string; status: number; sort: string }> = [
  { key: 'unmoderated', label: 'Новые заявки', status: BID_STATUS.NEW, sort: '-id' },
  { key: 'premoderation', label: 'Премодерация', status: BID_STATUS.PREMODERATION, sort: '-id' },
  { key: 'work', label: 'В работе', status: BID_STATUS.IN_WORK, sort: '-status_change_time' },
  { key: 'finish', label: 'Ремонт завершен', status: BID_STATUS.DONE, sort: '-status_change_time' },
  { key: 'cancel', label: 'Отмененные заявки', status: BID_STATUS.CANCELLED, sort: '-status_change_time' },
];

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id' },
  { value: 'description', label: 'Поиск по описанию' },
  { value: 'work_type_name', label: 'Поиск по типу работ' },
];

const BID_TYPE_OPTIONS = [
  { value: '1', label: 'Прямая' },
  { value: '2', label: 'Авто' },
];

const REQUEST_TYPE_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'client', label: 'Клиентская' },
  { value: 'test', label: 'Тестовая' },
  { value: 'duplicate', label: 'Дубль' },
];

const EVACUATOR_OPTIONS = [
  { value: '0', label: 'Не эвакуировать' },
  { value: '1', label: 'Эвакуировать' },
];

const WORK_ORDER_OPTIONS = [
  { value: '0', label: 'Нет' },
  { value: '1', label: 'Да' },
];

const DEVICE_OPTIONS = ['Web', 'Webview', 'Android', 'IOS'].map((v) => ({ value: v, label: v }));

const DEFAULT_COUNTRY_ID = '1';

const emptyFilters = {
  searchKey: '',
  searchValue: '',
  bidType: '',
  changedCostOnly: false,
  countryId: DEFAULT_COUNTRY_ID,
  cityId: '',
  requestType: '',
  carTypeId: '',
  markId: '',
  modelId: '',
  needEvacuator: '',
  createFrom: null as Date | null,
  createTo: null as Date | null,
  repairFrom: null as Date | null,
  repairTo: null as Date | null,
  source: '',
  partnerId: '',
  utmId: '',
  workOrder: '',
  cost: '',
  promoCode: '',
};

type Filters = typeof emptyFilters;

const { CUSTOMER_URL } = __CONFIG__;

const isStatusDateShown = (tab: TabKey) => tab !== 'unmoderated' && tab !== 'premoderation';

const formatWorkTypes = (workType: BidRow['work_type']) => {
  if (Array.isArray(workType)) return workType.map((item) => item.title).join(', ');
  return typeof workType === 'string' ? workType : '';
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="bids-filter-field">
    <span className="bids-filter-label">{label}</span>
    {children}
  </div>
);

const selectProps = { width: '100%' as const };

export const BidsPage = () => {
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const isOperator = 'callcenter-operator' in profile.roles;
  const [tab, setTab] = useState<TabKey>('unmoderated');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(TABS[0].sort);
  const [draft, setDraft] = useState<Filters>({ ...emptyFilters });
  const [applied, setApplied] = useState<Filters>({ ...emptyFilters });
  const [phones, setPhones] = useState<Record<string, string>>({});
  const [cancelBidId, setCancelBidId] = useState<number | null>(null);
  const [editBidId, setEditBidId] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');

  const countriesQuery = useQuery({ queryKey: ['active-countries'], queryFn: fetchActiveCountries });
  const utmQuery = useQuery({ queryKey: ['utm-list'], queryFn: fetchUtmList });
  const promoQuery = useQuery({ queryKey: ['bid-promo-codes'], queryFn: fetchPromoCodes });
  const carTypesQuery = useQuery({ queryKey: ['car-types'], queryFn: fetchCarTypes });
  const citiesQuery = useQuery({
    queryKey: ['cities-by-country', draft.countryId],
    queryFn: () => fetchCitiesByCountry(draft.countryId),
    enabled: Boolean(draft.countryId),
  });
  const marksQuery = useQuery({
    queryKey: ['car-marks', draft.carTypeId],
    queryFn: () => fetchCarMarks(draft.carTypeId),
    enabled: Boolean(draft.carTypeId),
  });
  const modelsQuery = useQuery({
    queryKey: ['car-models', draft.markId],
    queryFn: () => fetchCarModels(draft.markId),
    enabled: Boolean(draft.markId),
  });

  const activeTab = TABS.find((t) => t.key === tab) ?? TABS[0];

  const listParams = useMemo<BidsListParams>(
    () => ({
      page,
      sort_field: sortField,
      status_id: activeTab.status,
      id: applied.searchKey === 'id' ? applied.searchValue : '',
      description: applied.searchKey === 'description' ? applied.searchValue : '',
      work_type_name: applied.searchKey === 'work_type_name' ? applied.searchValue : '',
      country_id: applied.countryId,
      city_id: applied.cityId,
      type_id: applied.bidType,
      request_type: applied.requestType,
      mark_id: applied.markId,
      model_id: applied.modelId,
      need_evacuator: applied.needEvacuator,
      source: applied.source,
      partner_id: applied.partnerId,
      utm_id: applied.utmId,
      work_order: applied.workOrder,
      cost: applied.cost,
      promo_code: applied.promoCode,
      changed_cost_only: applied.changedCostOnly ? 1 : '',
      create_time_from: dateToUnix(applied.createFrom),
      create_time_to: dateToUnix(applied.createTo),
      repair_time_from: dateToUnix(applied.repairFrom),
      repair_time_to: dateToUnix(applied.repairTo),
    }),
    [applied, page, sortField, activeTab.status],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['bids', listParams],
    queryFn: () => fetchBidsList(listParams),
  });

  const rows = getListRows<BidRow>(data, ['bids']);
  const totalPages = getTotalPages(data);

  const phoneMutation = useMutation({
    mutationFn: ({ customerId }: { customerId: string | number; bidId: number }) =>
      showBidCustomerPhone(customerId),
    onSuccess: (res, { bidId }) => {
      if (res.success === 1 && res.phone) {
        setPhones((prev) => ({ ...prev, [String(bidId)]: res.phone! }));
      }
    },
  });

  const operatorStatusQuery = useQuery({
    queryKey: ['operator-work-status', profile.id],
    queryFn: () => fetchOperatorWorkStatus(profile.id!),
    enabled: isOperator && profile.id != null,
  });
  const operatorBusy = operatorStatusQuery.isFetching;
  const operatorWorking = Number(operatorStatusQuery.data?.status) === 1;

  const toggleOperatorWork = useMutation({
    mutationFn: () => (operatorWorking ? finishOperatorWork() : startOperatorWork()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operator-work-status', profile.id] });
    },
  });

  const atWorkMutation = useMutation({
    mutationFn: ({ bidId, serviceId }: { bidId: number; serviceId: number | string }) =>
      setBidAtWork(bidId, serviceId),
    onSuccess: () => {
      setActionError('');
      void qc.invalidateQueries({ queryKey: ['bids'] });
    },
    onError: (err: Error) => setActionError(err.message || 'Не удалось взять в работу'),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ reasonId, description }: { reasonId: string; description: string }) =>
      cancelBid(cancelBidId!, reasonId, description),
    onSuccess: () => {
      setCancelBidId(null);
      setActionError('');
      void qc.invalidateQueries({ queryKey: ['bids'] });
    },
    onError: (err: Error) => setActionError(err.message || 'Не удалось отменить заявку'),
  });

  const setDraftField = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const selectTab = (next: (typeof TABS)[number]) => {
    setTab(next.key);
    setSortField(next.sort);
    setPage(1);
  };

  const toggleSort = (field: string) => {
    setSortField((prev) => (prev === field ? `-${field}` : prev === `-${field}` ? field : `-${field}`));
    setPage(1);
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const onReset = (e: FormEvent) => {
    e.preventDefault();
    const next = { ...emptyFilters, countryId: '' };
    setDraft(next);
    setApplied(next);
    setPage(1);
  };

  const sortActive = (field: string) => sortField === field || sortField === `-${field}`;

  return (
    <>
      <section className="content-header">
        <h1>Заявки</h1>
        <div className="row">
          <div className="col-sm-12">
            <Link to="/bids/commissions" className="btn btn-primary">
              Отчёт по комиссиям
            </Link>
            {isOperator && Number(operatorStatusQuery.data?.success) === 1 && (
              <button
                type="button"
                className={`btn pull-right ${operatorWorking ? 'btn-success' : 'btn-warning'}`}
                disabled={operatorBusy || toggleOperatorWork.isPending}
                onClick={() => toggleOperatorWork.mutate()}
              >
                {operatorWorking ? 'Завершить работу' : 'Начать работу'}
              </button>
            )}
          </div>
        </div>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Список заявок ({rows.length})</h3>
            <form onSubmit={onSearch} className="bids-filters">
              <div className="bids-filters-grid">
                <div className="bids-filters-col">
                  <Field label="Поиск">
                    <FilterSelect
                      {...selectProps}
                      options={SEARCH_KEY_OPTIONS}
                      value={draft.searchKey}
                      onChange={(v) => setDraftField('searchKey', v)}
                    />
                    <input
                      className="form-control bids-filter-input"
                      style={{ marginTop: 6 }}
                      placeholder="Поиск..."
                      value={draft.searchValue}
                      onChange={(e) => setDraftField('searchValue', e.target.value)}
                    />
                  </Field>
                  <Field label="Фильтрация по типу">
                    <FilterSelect
                      {...selectProps}
                      options={BID_TYPE_OPTIONS}
                      value={draft.bidType}
                      onChange={(v) => setDraftField('bidType', v)}
                    />
                  </Field>
                  <label className="bids-filter-checkbox">
                    <input
                      type="checkbox"
                      checked={draft.changedCostOnly}
                      onChange={(e) => setDraftField('changedCostOnly', e.target.checked)}
                    />
                    Только с изменённой стоимостью
                  </label>
                </div>
                <div className="bids-filters-col">
                  <Field label="Фильтрация по стране">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(countriesQuery.data)}
                      value={draft.countryId}
                      isLoading={countriesQuery.isLoading}
                      onChange={(countryId) => setDraft((p) => ({ ...p, countryId, cityId: '' }))}
                    />
                  </Field>
                  <Field label="Фильтрация по городу">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(citiesQuery.data)}
                      value={draft.cityId}
                      isLoading={citiesQuery.isFetching}
                      onChange={(v) => setDraftField('cityId', v)}
                    />
                  </Field>
                </div>
                <div className="bids-filters-col">
                  <Field label="Выберите тип заявки">
                    <FilterSelect
                      {...selectProps}
                      options={REQUEST_TYPE_OPTIONS}
                      value={draft.requestType}
                      onChange={(v) => setDraftField('requestType', v)}
                      isClearable={false}
                    />
                  </Field>
                  <Field label="Выберите тип">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(carTypesQuery.data)}
                      value={draft.carTypeId}
                      isLoading={carTypesQuery.isLoading}
                      onChange={(carTypeId) => setDraft((p) => ({ ...p, carTypeId, markId: '', modelId: '' }))}
                    />
                  </Field>
                  <Field label="Фильтрация по марке">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(marksQuery.data)}
                      value={draft.markId}
                      isLoading={marksQuery.isFetching}
                      onChange={(markId) => setDraft((p) => ({ ...p, markId, modelId: '' }))}
                    />
                  </Field>
                  <Field label="Фильтрация по модели">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(modelsQuery.data)}
                      value={draft.modelId}
                      isLoading={modelsQuery.isFetching}
                      onChange={(v) => setDraftField('modelId', v)}
                    />
                  </Field>
                  <Field label="Фильтрация по необходимости эвакуатора">
                    <FilterSelect
                      {...selectProps}
                      options={EVACUATOR_OPTIONS}
                      value={draft.needEvacuator}
                      onChange={(v) => setDraftField('needEvacuator', v)}
                    />
                  </Field>
                </div>
                <div className="bids-filters-col">
                  <Field label="От даты создания">
                    <FilterDateTime value={draft.createFrom} onChange={(v) => setDraftField('createFrom', v)} />
                  </Field>
                  <Field label="До даты создания">
                    <FilterDateTime value={draft.createTo} onChange={(v) => setDraftField('createTo', v)} />
                  </Field>
                  <Field label="От даты ремонта">
                    <FilterDateTime value={draft.repairFrom} onChange={(v) => setDraftField('repairFrom', v)} />
                  </Field>
                  <Field label="До даты ремонта">
                    <FilterDateTime value={draft.repairTo} onChange={(v) => setDraftField('repairTo', v)} />
                  </Field>
                </div>
                <div className="bids-filters-col">
                  <Field label="Фильтрация по устройству">
                    <FilterSelect
                      {...selectProps}
                      options={DEVICE_OPTIONS}
                      value={draft.source}
                      onChange={(v) => setDraftField('source', v)}
                    />
                  </Field>
                  <Field label="Фильтрация по партнеру">
                    <FilterSelect
                      {...selectProps}
                      options={PARTNER_OPTIONS}
                      value={draft.partnerId}
                      onChange={(v) => setDraftField('partnerId', v)}
                    />
                  </Field>
                  <Field label="Фильтрация по источнику">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(utmQuery.data)}
                      value={draft.utmId}
                      isLoading={utmQuery.isLoading}
                      onChange={(v) => setDraftField('utmId', v)}
                    />
                  </Field>
                  <Field label="Фильтрация по заказу-наряду">
                    <FilterSelect
                      {...selectProps}
                      options={WORK_ORDER_OPTIONS}
                      value={draft.workOrder}
                      onChange={(v) => setDraftField('workOrder', v)}
                    />
                  </Field>
                  <Field label="Фильтрация по итоговой стоимости">
                    <input
                      type="number"
                      className="form-control bids-filter-input"
                      placeholder="Число..."
                      value={draft.cost}
                      onChange={(e) => setDraftField('cost', e.target.value)}
                    />
                  </Field>
                  <Field label="Лояльность">
                    <FilterSelect
                      {...selectProps}
                      options={toOptions(promoQuery.data)}
                      value={draft.promoCode}
                      isLoading={promoQuery.isLoading}
                      onChange={(v) => setDraftField('promoCode', v)}
                    />
                  </Field>
                </div>
              </div>
              <div className="bids-filters-actions">
                <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
                  Очистить все поля
                </button>{' '}
                <button type="submit" className="btn btn-primary" disabled={isFetching}>
                  Найти
                </button>
              </div>
            </form>
          </div>

          <div className="box-body">
            <ul className="nav nav-pills" style={{ marginBottom: 10 }}>
              {TABS.map((t) => (
                <li key={t.key} className={t.key === tab ? 'active' : ''}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      selectTab(t);
                    }}
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>

            {isLoading && <p>Загрузка…</p>}
            {error && <div className="alert alert-danger">Ошибка загрузки заявок</div>}
            {actionError && <div className="alert alert-danger">{actionError}</div>}
            {!isLoading && !error && rows.length === 0 && <p>Нет результатов</p>}
            {rows.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <SortableTh active={sortActive('id')} onClick={() => toggleSort('id')}>
                        id
                      </SortableTh>
                      <th>Тип заявки</th>
                      <SortableTh active={sortActive('mark_id')} onClick={() => toggleSort('mark_id')}>
                        Марка
                      </SortableTh>
                      <SortableTh active={sortActive('model_id')} onClick={() => toggleSort('model_id')}>
                        Модель
                      </SortableTh>
                      {isStatusDateShown(tab) && (
                        <SortableTh
                          active={sortActive('status_change_time')}
                          onClick={() => toggleSort('status_change_time')}
                        >
                          Дата перевода заявки
                        </SortableTh>
                      )}
                      <SortableTh active={sortActive('id')} onClick={() => toggleSort('id')}>
                        Дата создания заявки
                      </SortableTh>
                      <th>Тип работ</th>
                      <th>Скидка</th>
                      <th>Тип заявки</th>
                      <th>Направление</th>
                      <th>Источник</th>
                      <th>Лояльность</th>
                      <th>Партнер</th>
                      <th>Устройства</th>
                      <th>Имя</th>
                      <th>Телефон</th>
                      <SortableTh active={sortActive('city_id')} onClick={() => toggleSort('city_id')}>
                        Город
                      </SortableTh>
                      <SortableTh active={sortActive('counter')} onClick={() => toggleSort('counter')}>
                        Признак заявки через AI
                      </SortableTh>
                      {tab === 'finish' && (
                        <>
                          <th>Дата завершения ремонта</th>
                          <th>Отзыв</th>
                        </>
                      )}
                      {tab === 'cancel' && (
                        <>
                          <th>Дата завершения ремонта</th>
                          <th>Причина отмены заявки</th>
                        </>
                      )}
                      <th />
                      <th />
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((bid) => {
                      const customer = bid.customer;
                      const phone = phones[String(bid.id)] ?? customer?.phone;
                      const isGazpromTire = bid.type_id === 1 && bid.partner_id === 1 && Boolean(bid.work_type);
                      const discount = bid.applied_discount;
                      const serviceId = bid.bid_distribution_services?.[0]?.service_id;
                      const canCancel =
                        bid.status_id !== BID_STATUS.CANCELLED && bid.status_id !== BID_STATUS.DONE;
                      const canTakeToWork =
                        tab === 'unmoderated' && Number(bid.status_id) === BID_STATUS.NEW && serviceId != null;

                      return (
                        <tr key={bid.id}>
                          <td>
                            <Link to={`/bids/bid/${bid.id}`}>{bid.id}</Link>
                            {isGazpromTire && <div className="id-customer-box">шиномонтаж</div>}
                            {bid.type_id === 1 && !isGazpromTire && <div className="id-customer-box">прямая</div>}
                            {bid.status_id === BID_STATUS.PREMODERATION && tab !== 'premoderation' && (
                              <div className="id-customer-box">премодерация</div>
                            )}
                          </td>
                          <td>{bid.request_type?.title}</td>
                          <td>{bid.auto?.mark?.name ?? 'нет марки'}</td>
                          <td>{bid.auto?.model?.name ?? 'нет модели'}</td>
                          {isStatusDateShown(tab) && <td>{formatUnixDateTime(bid.status_change_time)}</td>}
                          <td>{formatUnixDateTime(bid.create_time)}</td>
                          <td style={{ maxWidth: 300, whiteSpace: 'normal' }}>{formatWorkTypes(bid.work_type)}</td>
                          <td>
                            {discount
                              ? `${discount.applied_discount_percent}% ${discount.discount_name ?? ''}`
                              : 'Нет скидки'}
                          </td>
                          <td>{BID_CREATED_TYPE_FROM[bid.is_created_from_call ?? -1]}</td>
                          <td>
                            <Link to={`/bids/repair-referral/${bid.id}`}>Направление</Link>
                          </td>
                          <td>{bid.utm_source}</td>
                          <td>{bid.attached_promo_code?.code}</td>
                          <td>{bid.partner_name}</td>
                          <td>{bid.source}</td>
                          <td>
                            <span>{customer?.name ?? 'нет автовладельца'}</span>{' '}
                            {customer?.id != null && (
                              <UserProfileRedirect
                                userId={customer.id}
                                route={`${CUSTOMER_URL}/cabinet/customer/profile`}
                              >
                                <button type="button" className="btn btn-primary btn-sm">
                                  Открыть профиль
                                </button>
                              </UserProfileRedirect>
                            )}
                          </td>
                          <td>
                            {phone ? (
                              phone
                            ) : (
                              customer?.id != null && (
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={phoneMutation.isPending}
                                  onClick={() => phoneMutation.mutate({ customerId: customer.id!, bidId: bid.id })}
                                >
                                  Показать
                                </button>
                              )
                            )}
                          </td>
                          <td>{bid.city?.name ?? ''}</td>
                          <td>{bid.bid_source === 'ai' ? 'Да' : 'Нет'}</td>
                          {tab === 'finish' && (
                            <>
                              <td>{bid.final_date ? formatUnixDateTime(bid.final_date) : '-'}</td>
                              <td>{bid.has_review === 1 ? 'Да' : 'Нет'}</td>
                            </>
                          )}
                          {tab === 'cancel' && (
                            <>
                              <td>{bid.final_date ? formatUnixDateTime(bid.final_date) : '-'}</td>
                              <td>{bid.reject_reason?.[0]?.type}</td>
                            </>
                          )}
                          <td>
                            <button
                              type="button"
                              className="btn btn-success"
                              title="Редактировать"
                              onClick={() => setEditBidId(bid.id)}
                            >
                              <i className="fa fa-pencil" />
                            </button>
                          </td>
                          <td>
                            {canTakeToWork && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={atWorkMutation.isPending}
                                onClick={() =>
                                  atWorkMutation.mutate({ bidId: bid.id, serviceId: serviceId! })
                                }
                              >
                                В работу
                              </button>
                            )}
                          </td>
                          <td>
                            {canCancel && (
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => {
                                  setActionError('');
                                  setCancelBidId(bid.id);
                                }}
                              >
                                Отменить
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </section>
      {editBidId != null && <BidEditModal bidId={editBidId} onClose={() => setEditBidId(null)} />}
      {cancelBidId != null && (
        <CancelBidModal
          isSaving={cancelMutation.isPending}
          error={actionError}
          onClose={() => {
            setCancelBidId(null);
            setActionError('');
          }}
          onConfirm={(reasonId, description) => cancelMutation.mutate({ reasonId, description })}
        />
      )}
    </>
  );
};
